/**
 * Súpiska bojovníkov: mriežka portrétov s hľadaním. Hrateľné postavy idú
 * prvé; WIP postavy sú stlmené so zámkom — kliknúť na ne nejde, ale ich
 * balíček si hráč pozrieť môže (tlačidlo v paneli hrdinu).
 */

import type { HeroDef } from '@unmatched/engine';
import { IMPLEMENTED_HEROES } from '@unmatched/engine';
import { useMemo, useState } from 'react';

import { artOf } from '../screens/Home.js';

interface Props {
  heroes: HeroDef[];
  /** Kto má ktorú postavu — slot → heroId (na odznaky „1P", „2P"). */
  picks: (string | null)[];
  /** Slot, ktorému práve vyberáme. */
  activeSlot: number;
  onPick: (heroId: string) => void;
  /** Klik na WIP postavu — nedá sa vybrať, ale dá sa pozrieť balíček. */
  onInspect?: (hero: HeroDef) => void;
}

export function Roster({ heroes, picks, activeSlot, onPick, onInspect }: Props) {
  const [query, setQuery] = useState('');
  // WIP postavy sú vidieť hneď (stlmené) — súpiska s dvoma dlaždicami
  // pôsobila prázdne a hráč by nevedel, čo ešte pribudne.
  const [showWip, setShowWip] = useState(true);

  const playable = heroes.filter((h) => IMPLEMENTED_HEROES.includes(h.id));
  const found = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const list = heroes.filter((hero) => {
      if (!needle) return true;
      return [hero.name, hero.set ?? '', hero.attack === 'melee' ? 'zblízka' : 'na diaľku']
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
    return [...list].sort(
      (a, b) =>
        Number(IMPLEMENTED_HEROES.includes(b.id)) - Number(IMPLEMENTED_HEROES.includes(a.id)) ||
        a.name.localeCompare(b.name),
    );
  }, [heroes, query]);

  // Hrateľné sú vždy prvé; WIP sa dá skryť tlačidlom v pätičke.
  const visible = query || showWip ? found : found.filter((h) => IMPLEMENTED_HEROES.includes(h.id));
  const hidden = found.length - visible.length;

  return (
    <div className="roster">
      <header className="roster__head">
        <span className="panel__title panel__title--sm">Súpiska bojovníkov</span>
        <input
          className="search search--sm"
          type="search"
          value={query}
          placeholder="Hľadať…"
          onChange={(event) => setQuery(event.target.value)}
        />
      </header>

      <div className="roster__grid">
        {visible.map((hero) => {
          const ready = IMPLEMENTED_HEROES.includes(hero.id);
          const seats = picks.flatMap((id, slot) => (id === hero.id ? [slot] : []));
          const art = artOf(hero.id);
          return (
            <button
              key={hero.id}
              type="button"
              className={[
                'rosterTile',
                seats.includes(activeSlot) ? 'is-picked' : '',
                seats.length ? 'is-taken' : '',
                ready ? '' : 'is-wip',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-disabled={!ready}
              onClick={() => (ready ? onPick(hero.id) : onInspect?.(hero))}
            >
              <span className="rosterTile__art">
                {art ? (
                  <img src={art} alt="" loading="lazy" draggable={false} />
                ) : (
                  <span className="rosterTile__letter">{hero.name.slice(0, 1)}</span>
                )}
              </span>
              <span className="rosterTile__name">{hero.name}</span>
              {seats.length ? (
                <span className="rosterTile__seats">
                  {seats.map((slot) => (
                    <span key={slot} className={`seatDot seatDot--p${slot}`}>
                      {slot + 1}P
                    </span>
                  ))}
                </span>
              ) : null}
              {ready ? null : <span className="rosterTile__lock">WIP</span>}
            </button>
          );
        })}
      </div>

      <footer className="roster__foot">
        <span>
          {playable.length} hrateľné z {heroes.length}
        </span>
        {!query ? (
          <button type="button" className="linkBtn" onClick={() => setShowWip((v) => !v)}>
            {showWip ? 'len hrateľné' : `+ ${hidden} rozpracovaných (WIP)`}
          </button>
        ) : null}
      </footer>
    </div>
  );
}
